/**
 * WorkSession API client — search-only surface(spec §4.5 + §7.3)。
 *
 * 新 spec 把所有 work-session mutation 都搬去 `taskActions.{start,pause,resume}`
 * (`taskActionsService.ts`),WorkSession 在前端只剩一个只读端点:
 *
 *   POST /pms/task-work-sessions/search   — 工时聚合 / report 列表
 *   GET  /pms/task-work-sessions/{id}     — (本文件暂未暴露,待 consumer 出现)
 *
 * 典型 W7.1 用例:
 *   - "worker A 上周干了多少小时"
 *     filters: { assigneeId, startedAt: between [...] }
 *     aggregates: [{ alias: 'totalSeconds', operation: 'sum', field: 'durationSeconds' }]
 *   - "本月清洁工时总和" — filter task.taskTypeCode='cleaning' + 时间段
 *   - "某 task 的所有 session(含 helpers)" — filter taskId IN (X, ...child_ids)
 *
 * 不附带 `Idempotency-Key` header(只读 endpoint,且 spec §7.5 不暴露 header)。
 */
import axios from "@/api/axios-config";
import { parseApiError } from "./errors";
import type { BaseQuery } from "@/types/baseQuery";
import type { WorkSession } from "@/types/task";

/**
 * Search 响应 — 与其它 search endpoint
 * 一致的标准 shape:`{ results, pagination, summary? }`。
 *
 * `summary` 是 aggregate/summaryType 触发返回的额外标量(后端 BaseQuery
 * `includeSummary=true` 或 `aggregates` 字段触发);未启用时缺省。
 */
export interface WorkSessionSearchResponse {
  results: WorkSession[];
  pagination: {
    page: number;
    pageSize: number;
    total: number;
    totalPages: number;
  };
  summary?: Record<string, unknown>;
}

/** work_session search 的统一投影。三个消费方（AttendanceDetail /
 *  useDailyWorkDetail / useUserDayActivity）共用，改这里三处同时生效。
 *  listing 四列是 2026-08 map view 日活动时间线加的：nickname 给
 *  attendance 行显示房源名，经纬度给地图 marker。 */
export const WORK_SESSION_COLUMNS =
  'id taskId assigneeId startedAt endedAt durationSeconds ' +
  'task.id task.title task.taskTypeCode ' +
  'task.taskSubtypeCode task.status task.isPaused ' +
  'task.listingId task.listing.nickname task.listing.latitude task.listing.longitude'

export const workSessions = {
  /**
   * POST /pms/task-work-sessions/search — Filter DSL search。
   *
   * Body 直接转发(`BaseQuery` shape:`filters` / `page` / `pageSize` /
   * `orderBy` / `columns` / `q` / `includeSummary` / `aggregates`)。
   * DSL filter key 用 camelCase(如 `assigneeId` / `startedAt`),
   * SnowflakeId 字段必须 `type: 'string'` 否则前端精度丢失 / 后端拼 SQL 错。
   * 详见 `docs/SEARCH_AND_LIST_PARAMETER_GUIDE.md`。
   */
  search(body: BaseQuery): Promise<WorkSessionSearchResponse> {
    return (async () => {
      try {
        return (await axios.post(
          "/pms/task-work-sessions/search",
          body,
        )) as unknown as WorkSessionSearchResponse;
      } catch (err: unknown) {
        const response = (err as { response?: { data?: unknown; status?: number } })
          ?.response;
        if (response?.data) {
          throw parseApiError(response.status ?? 0, response.data);
        }
        throw err;
      }
    })();
  },
};
